/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {
  AssetMessage,
  InboundMessage,
  ObservableEntityId,
  ObservableEntityIds,
  SubscriptionResponse,
  SubscriptionResults
} from './types';

/**
 * Provides a utility which converts a SubscriptionResponse to SubscriptionResults.
 *
 * @param   The SubscriptionResponse object from which to generate the result set.
 * @returns A SubscriptionResults object generated from the provided SubscriptionResponse.
 */
export const getSubscriptionResults = ({message}: SubscriptionResponse): SubscriptionResults => {
  let items;
  for (const entity in message as any) {
    const {results, errors} = message[entity];
    items = {
      succeeded : results.success.ids,
      failed    : errors.failure.ids,
      entity
    };
  }
  return items || {};
};

/**
 * Provides a utility which returns the id for the received message based on
 * the pre-defined EntitySubscription identifiers
 *
 * @param   data The message object received from the underlying WebSocket.
 * @returns The `id` of the entity or task to which the message is associated.
 */
export const getEntityId = ({message}: InboundMessage): string | number => {
  const change = message[Object.keys(message)[0]];
  return change?.id || change?.assetId;
};

/**
 * Provides a utility which returns a consolidation of the updates for a given
 * change. In certain cases, the value for an attribute update may be wrapped
 * in an object, while in others the value is provided directly. This utility
 * abstracts these implementation details for convenience from clients.
 *
 * @param   data The change object from the received message for which the
 *          updates are to be determined.
 * @returns An object containing each changed attribute and it's value.
 */
export const getEntityUpdates = ({message}: InboundMessage): GenericObject => {
  const entity  = message[Object.keys(message)[0]];
  const change  = entity.change || entity;
  const changes = {};
  Object.keys(change).forEach(property => {
    const data = change[property]?.data;
    changes[property] = data?.value || data;
  });
  return {...changes};
};

/**
 * Provides a simple utility which allows for passing a single or multiple ids and
 * returns a single collection of ids.
 *
 * @param   ids either a single id, or multiple ids.
 * @returns a collection of stringified ids
 */
export const getIds = (ids: ObservableEntityIds): ObservableEntityId[] => {
  return Array.isArray(ids) ? ids.map(id => String(id)) : [String(ids)];
};

/**
 * Provides a convenience which generates the formatted entity subscription string
 * suitable for logging.
 *
 * @param   results The results of a subscription request.
 * @param   isAdd True when the message is to be generated for a subscription addition,
 *          and false when it is to be generated for a subscription removal.
 * @returns A formatted subscription results string.
 */
export const formatSubscriptionResults = (
  {succeeded, failed, entity}: SubscriptionResults, isAdd = true): string => {
  const context     = isAdd ? 'Added' : 'Removed';
  const mode        = isAdd ? 'Add' : 'Remove';
  const entityName  = `${capitalizeFirstLetter(entity)}`;
  const hasFailures = !!failed.length;
  const success     = `${succeeded.length} ${entityName}(s) Successfully ${context}: [${succeeded.toString()}]`;
  const failures    = hasFailures ? `${failed.length} ${entityName}(s) Failed to be ${context}: [${failed.toString()}]` : '';
  return `${entityName} Subscription Results. ${success}.${failures ? ` ${failures}.` : ''}.`;
};

/**
 * Provides a convenience which generates the formatted reconnection attempt string.
 * This helper is exclusively used by the SocketLogger during reconnection.
 *
 * @param   attempts The current number of attempts
 * @param   postfix when true (default), the attempt will include the appropriate
 *          postfix value (either 'st', 'nd', 'rd', or 'th').
 * @returns A formatted reconnection attempt string.
 */
export const formatReconnectionAttempts = (attempts: number, postfix = true): string => {
  if (postfix) {
    const postfixes = ['st', 'nd', 'rd'];
    const index = attempts > 20 ? (attempts -1) % 10 : (attempts -1);
    return `[${attempts}${postfixes[index] ||'th'} attempt]`;
  }
  return `${attempts} attempt${attempts > 1 ? 's' : ''}`;
};
